/**
 * Notification chime, synthesised with the Web Audio API.
 *
 * No audio file: an asset would need bundling (and a CSP-safe URL), while two
 * oscillators give the same soft two-note "ding" in a few lines.
 *
 * Browsers refuse to start an AudioContext until the page has seen a user
 * gesture, so `primeNotificationSound` warms one up on the first click/keypress
 * and every later `playNotificationChime()` reuses it. Before that first
 * gesture the call is a silent no-op rather than an unhandled rejection.
 */

let ctx: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

/** Attach a one-shot listener so the first user gesture unlocks audio playback. */
export function primeNotificationSound(): () => void {
  const unlock = () => {
    const c = audioContext();
    if (c && c.state === "suspended") void c.resume();
  };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
  return () => {
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
}

/** Two descending notes, ~0.5s, deliberately quiet. Never throws. */
export function playNotificationChime(): void {
  const c = audioContext();
  if (!c) return;
  try {
    if (c.state === "suspended") void c.resume();
    // Still locked (no gesture yet) - skip rather than queue a burst of sound.
    if (c.state !== "running") return;

    const now = c.currentTime;
    // E6 then C#6: a gentle major-third fall, the classic notification interval.
    [
      { freq: 1318.5, at: 0, dur: 0.28 },
      { freq: 1108.7, at: 0.13, dur: 0.34 },
    ].forEach(({ freq, at, dur }) => {
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.09, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + dur);
      osc.connect(gain).connect(c.destination);
      osc.start(now + at);
      osc.stop(now + at + dur + 0.02);
    });
  } catch {
    /* audio is a nicety - never let it surface to the user */
  }
}
